import React, { useState, useEffect, useContext } from "react"
import Loader from "../../../../components/ui/loader"
import NewCardModal from "./NewCardModal"
import { Button } from "../../../../components/ui/button"
import {
  SvgPlus,
  SvgChevronDown,
  SvgAmericanExpress,
  SvgVisa,
  SvgMastercard,
  SvgDiscover,
  SvgDinersClub,
} from "../../../../components/icons"
import { PaymentRequests } from "../../../../api/app.service"
import { useToast } from "../../../../hooks/useToast"
import ConfirmCardDeletion from "./ConfirmCardDeletion"
import { NotificationCtx } from "../../../../context/notificationContext"
import { faIR } from "date-fns/locale"

interface SavedCardsProps {
  loading: boolean
  user: any
  getPaymentMethods: () => void
  localPaymentMethods: any[]
  setLocalPaymentMethods: React.Dispatch<React.SetStateAction<any[]>>
}

const SavedCards: React.FC<SavedCardsProps> = ({
  loading,
  user,
  getPaymentMethods,
  localPaymentMethods,
  setLocalPaymentMethods,
}) => {
  const { toast } = useToast()
  const [isOpen, setIsOpen] = useState(false)
  const [showNewCardModal, setShowNewCardModal] = useState(false)
  const [loadingDelete, setLoadingDelete] = useState(false)
  const [showCardDeletModal, setShowCardDeleteModal] = useState(false)
  const [selectedCardToDelete, setSelectedCardToDelete] = useState<any>(null)
  const { lastWebsocketMessage } = useContext(NotificationCtx)
  const [cardSave, setCardSave] = useState(false)

  const deletePayment = async (token: string) => {
    setLoadingDelete(true)

    try {
      const res = await PaymentRequests.deletePaymentToken({
        user_id: parseInt(user.user_id),
        payment_method_token: token,
      })

      if (res?.message?.includes("purchase attached")) {
        toast.error(res.message)
        return
      }

      setLocalPaymentMethods((prev) =>
        prev.filter((card) => card.payment_method_token !== token)
      )

      toast.success("Card deleted successfully")
      setShowCardDeleteModal(false)
    } catch (error: any) {
      console.error("Error deleting card:", error)
      toast.error(
        error?.response?.data?.message ||
          error?.message ||
          "Failed to delete card."
      )
    } finally {
      setLoadingDelete(false)
    }
  }

  useEffect(() => {
    if (
      lastWebsocketMessage?.type === "payment" &&
      (lastWebsocketMessage?.data?.event_type === "card_added")
    ) {
      getPaymentMethods()
      setCardSave(false)
    }
  }, [lastWebsocketMessage])

  return (
    <div className="p-6 w-full">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex justify-between items-center border-b border-grayMist p-2 mb-6 w-full cursor-pointer select-none bg-transparent"
      >
        <h2 className="text-left text-[22px] font-bold">Saved Cards</h2>
        <SvgChevronDown
          className={`h-10 w-10 transform transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      {!isOpen && (
        <div className="w-full items-center text-grayCharcoal border border-grayMist rounded-lg py-3 px-4 shadow-sm bg-white">
          <Button
            className="text-[20px] flex items-center gap-2"
            onClick={() => setShowNewCardModal(true)}
          >
            <SvgPlus /> Add Card
          </Button>
        </div>
      )}

      {!isOpen && (
        <div className="flex justify-center mt-4 mb-4">
          <Button
            onClick={() => setIsOpen(true)}
            variant="outline"
            className="rounded-full shadow-sm transition-all duration-200"
          >
            View Saved Cards
          </Button>
        </div>
      )}

      {isOpen && (
        <div className="transition-all duration-300 ease-in-out">
          {cardSave && 
            <div className="flex justify-start py-10 z-10 relative sm:px-6 px-3 items-center gap-2">
            <Loader />
            <span className="italic text-sm text-gray-600">
              It may take a few minutes for your card to appear.
            </span>
          </div>
        }
          <div className="hidden sm:grid sm:grid-cols-4 text-[18px] font-semibold text-gray-700 p-2 mb-3">
            <span className="text-left">Name</span>
            <span className="text-center">Card Number</span>
            <span className="text-center">Expiration</span>
            <span className="text-center"></span>
          </div>

          <div className="flex flex-col gap-4 mb-4">
            {loading ? (
              <div className="flex justify-center py-10">
                <Loader />
              </div>
            ) : localPaymentMethods.length > 0 ? (
              localPaymentMethods.map((card) => {
                const brand = card.brand?.toLowerCase()
                const Icon =
                  brand === "visa"
                    ? SvgVisa
                    : brand === "mastercard"
                    ? SvgMastercard
                    : brand === "american express" || brand === "amex"
                    ? SvgAmericanExpress
                    : brand === "discover"
                    ? SvgDiscover
                    : brand === "dinersclub"
                    ? SvgDinersClub
                    : null

                return (
                  <div
                    key={card.id}
                    className="text-[18px] text-grayCharcoal border border-grayMist rounded-lg py-3 px-4 shadow-sm bg-white"
                  >
                    {/* Mobile (<sm) */}
                    <div className="block sm:hidden space-y-2">
                      <div className="text-left font-medium">
                        {card.name || "—"}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <div className="border border-grayMist rounded-md px-2 py-0 flex items-center justify-center">
                            {Icon && <Icon className="w-6 h-6" />}
                          </div>
                          <span>•••• {card.last_digits}</span>
                        </div>
                        <span>
                          {card.expiry ? card.expiry.replace("-", "/") : "—"}
                        </span>
                      </div>

                      <div className="flex justify-end">
                        <Button
                          variant="outlineDynamic"
                          size="lg"
                          className="text-[18px] w-full"
                          style={{ color: "red", borderColor: "red" }}
                          onClick={() => {
                            setSelectedCardToDelete(card)
                            setShowCardDeleteModal(true)
                          }}
                          disabled={loadingDelete}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>

                    {/* Desktop (≥sm) */}
                    <div className="hidden sm:grid sm:grid-cols-4 items-center gap-2">
                      <span className="text-left font-medium">
                        {card.name || "—"}
                      </span>
                      <span className="text-center flex items-center justify-center gap-2">
                        <div className="border border-grayMist rounded-md px-2 py-0 flex items-center justify-center">
                          {Icon && <Icon className="w-6 h-6" />}
                        </div>
                        •••• {card.last_digits}
                      </span>
                      <span className="text-center">
                        {card.expiry ? card.expiry.replace("-", "/") : "—"}
                      </span>
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="outlineDynamic"
                          size="lg"
                          className="text-[18px]"
                          style={{ color: "#FF4A00", borderColor: "#FF4A00" }}
                          onClick={() => {
                            setSelectedCardToDelete(card)
                            setShowCardDeleteModal(true)
                          }}
                          disabled={loadingDelete}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                )
              })
            ) : (
              <div className="text-center text-gray-500 text-[16px] py-10">
                No saved cards found
              </div>
            )}
          </div>

          <div className="w-full items-center text-grayCharcoal border border-grayMist rounded-lg py-3 px-4 shadow-sm bg-white">
            <Button
              className="text-[20px] flex items-center gap-2"
              onClick={() => setShowNewCardModal(true)}
            >
              <SvgPlus /> Add Card
            </Button>
          </div>
        </div>
      )}

      {showNewCardModal && (
        <NewCardModal
          setShowNewCardModal={setShowNewCardModal}
          onCardAdded={getPaymentMethods}
          setCardSave={setCardSave}
        />
      )}
      {showCardDeletModal && selectedCardToDelete && (
        <ConfirmCardDeletion
          setShowCardDeleteModal={setShowCardDeleteModal}
          onConfirmDelete={() =>
            deletePayment(selectedCardToDelete.payment_method_token)
          }
          lastFour={selectedCardToDelete.last_digits}
          brand={selectedCardToDelete.brand}
        />
      )}
    </div>
  )
}

export default SavedCards
